import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { formatDayLabel, formatInt, formatTokens } from "./formatters";

type DayRow = { day: string; responses: number; value: number };

function CustomTooltip({
  active,
  payload,
  valueLabel,
  formatValue,
  color,
}: {
  active?: boolean;
  payload?: Array<{ payload: DayRow }>;
  valueLabel: string;
  formatValue: (v: number) => string;
  color: string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="border-cyber-border bg-cyber-surface text-cyber-text rounded border px-2 py-1.5 text-[10px] shadow-lg">
      <div className="font-medium">{row.day}</div>
      <div className="text-cyber-muted">
        {formatInt(row.responses)} responses &middot;{" "}
        <span style={{ color }}>
          {formatValue(row.value)} {valueLabel}
        </span>
      </div>
    </div>
  );
}

export function DailyBarChart({
  data,
  color = "rgb(var(--cyber-cyan))",
  valueLabel = "tokens",
  formatValue = formatTokens,
}: {
  data: DayRow[];
  color?: string;
  valueLabel?: string;
  formatValue?: (v: number) => string;
}) {
  if (data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
        <CartesianGrid
          vertical={false}
          stroke="rgb(var(--cyber-border))"
          strokeDasharray="3 3"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "rgb(var(--cyber-muted))", fontSize: 10 }}
          tickFormatter={formatDayLabel}
          tickMargin={8}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: "rgb(var(--cyber-muted))", fontSize: 10 }}
          tickFormatter={formatValue}
          width={48}
        />
        <Tooltip
          content={
            <CustomTooltip valueLabel={valueLabel} formatValue={formatValue} color={color} />
          }
          cursor={{ fill: "rgb(var(--cyber-surface))", opacity: 0.5 }}
        />
        <Bar dataKey="value" fill={color} opacity={0.75} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
